/*
 * cart-recommended.js — ported from orion-v3.0's
 * src/js/includes/cart-recommended.js.
 *
 * <cart-recommended> fetches a single recommended product from
 * Shopify's recommendations endpoint (URL on the data attribute,
 * built in cart-recommended.liquid) and replaces its own innerHTML
 * with the returned card. Re-fetches on every `cart:updated`
 * dispatched by assets/ajax-cart.js, debounced 150ms.
 *
 * No jQuery / ES module imports in the original — v3.0 ships this
 * file as-is and so does H3.0.
 */

class CartRecommended extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this._onCartUpdated = this.onCartUpdated.bind(this);
    this._loadTimeout = null;
    this._lastInnerHTML = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    document.addEventListener('cart:updated', this._onCartUpdated);

    const container = this.querySelector('.cart-recommended__container');
    if (container && container.innerHTML.trim()) {
      this.initializeProductForms();
      this.style.display = 'block';
    } else {
      this.loadRecommendations();
    }
  }

  async onCartUpdated(/* event */) {
    clearTimeout(this._loadTimeout);
    this._loadTimeout = setTimeout(() => {
      this.loadRecommendations();
    }, 150);
  }

  async loadRecommendations() {
    const url = this.dataset.recommendationsUrl;

    if (!url) {
      console.warn(
        '[CartRecommended] Missing data-recommendations-url attribute',
      );
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const container =
        doc.querySelector('[data-recommendations-container]') ||
        doc.querySelector('.cart-recommended__container') ||
        doc.querySelector('.cart-recommended');

      if (container && container.innerHTML.trim()) {
        const newInnerHTML = container.innerHTML.trim();

        if (this._lastInnerHTML && this._lastInnerHTML === newInnerHTML) {
          this.style.display = 'block';
          return;
        }
        container.querySelectorAll('cart-recommended').forEach((nested) => {
          const children = Array.from(nested.childNodes);
          nested.replaceWith(...children);
        });

        this.innerHTML = newInnerHTML;
        this._lastInnerHTML = newInnerHTML;

        this.style.display = 'block';

        this.initializeProductForms();
      } else {
        this.style.display = 'none';
      }
    } catch (error) {
      console.error('[CartRecommended] Error loading recommendations:', error);
      this.style.display = 'none';
    }
  }

  disconnectedCallback() {
    if (this._onCartUpdated)
      document.removeEventListener('cart:updated', this._onCartUpdated);
    if (this._loadTimeout) clearTimeout(this._loadTimeout);
  }

  initializeProductForms() {
    const form =
      this.querySelector('.cart-recommended__form') ||
      this.querySelector('form');
    const submitButton =
      form?.querySelector('[type="submit"]') ||
      this.querySelector('.cart-recommended__add-button');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.classList.remove('disabled');
      submitButton.setAttribute('aria-disabled', 'false');
    }
  }
}

customElements.define('cart-recommended', CartRecommended);
